import React, { useState, useEffect, useRef } from "react";
import {
  obtenerInfoIA,
  generarResumenIA,
  preguntarChatIA,
  subirDocumento,
} from "../api";
import {
  HiX,
  HiOutlineDocumentText,
  HiOutlineChatAlt2,
  HiOutlineDownload,
  HiOutlineClipboardCopy,
  HiOutlineRefresh,
  HiOutlineGlobeAlt,
  HiOutlineFolderOpen,
  HiOutlinePaperAirplane,
  HiOutlineSparkles,
  HiOutlineCloudUpload,
  HiOutlineTrash,
} from "react-icons/hi";
import { toast } from "react-toastify";
import "../styles.css";

const PREGUNTAS_RAPIDAS = [
  "¿Qué solvencia técnica y económica exigen?",
  "¿Cuáles son los criterios de adjudicación y su puntuación?",
  "¿Qué garantías (provisional/definitiva) y plazos aplican?",
  "¿Hay condiciones especiales, límites de subcontratación o penalizaciones?",
  "Hazme un checklist de requisitos para presentar la oferta",
];

function parsearSeccionesResumen(texto) {
  if (!texto) return [];
  const lineas = texto.split(/\r?\n/);
  const secciones = [];
  let actual = null;

  const regexTitulo = /^\s*(\d+)\.\s+(.+)$/;

  for (const linea of lineas) {
    const match = linea.match(regexTitulo);
    if (match) {
      if (actual) secciones.push(actual);
      actual = {
        numero: match[1],
        titulo: match[2].trim(),
        contenido: [],
      };
    } else {
      if (!actual) {
        if (linea.trim()) {
          actual = {
            numero: "ℹ️",
            titulo: "Nota General",
            contenido: [linea],
          };
        }
      } else {
        actual.contenido.push(linea);
      }
    }
  }
  if (actual) secciones.push(actual);
  return secciones;
}

function PanelIA({
  numeroExpediente,
  enlaceDefecto = null,
  tabInicial = "resumen",
  onCerrar,
  onActualizarDocumentos,
}) {
  const [activeTab, setActiveTab] = useState(tabInicial);
  const [infoIA, setInfoIA] = useState(null);
  const [cargandoInfo, setCargandoInfo] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [docSeleccionado, setDocSeleccionado] = useState("");
  const [subiendoPdf, setSubiendoPdf] = useState(false);

  // Estado del Chat
  const [mensajes, setMensajes] = useState([]);
  const [preguntaInput, setPreguntaInput] = useState("");
  const [enviandoChat, setEnviandoChat] = useState(false);
  const [fuentesChat, setFuentesChat] = useState([]);
  const panelRef = useRef(null);
  const messagesBoxRef = useRef(null);
  const fileUploadRef = useRef(null);

  useEffect(() => {
    setActiveTab(tabInicial);
    setTimeout(() => {
      if (panelRef.current) {
        panelRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 60);
  }, [tabInicial]);

  const cargarInfo = async () => {
    try {
      setCargandoInfo(true);
      const data = await obtenerInfoIA(numeroExpediente);
      setInfoIA(data);
    } catch (err) {
      console.error("Error obteniendo info IA:", err);
    } finally {
      setCargandoInfo(false);
    }
  };

  useEffect(() => {
    cargarInfo();
  }, [numeroExpediente]);

  useEffect(() => {
    if (messagesBoxRef.current && mensajes.length > 0) {
      messagesBoxRef.current.scrollTo({
        top: messagesBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [mensajes, enviandoChat]);

  const handleGenerar = async (origen = "auto", docEsp = null) => {
    setGenerando(true);
    const etiqueta =
      origen === "local"
        ? "Analizando documentos subidos con IA..."
        : origen === "enlace"
        ? "Extrayendo pliegos del portal y resumiendo con IA..."
        : "Generando resumen inteligente con IA...";
    const toastId = toast.loading(`✨ ${etiqueta}`);

    try {
      const res = await generarResumenIA(numeroExpediente, {
        enlace: enlaceDefecto || infoIA?.enlace_principal || null,
        origen,
        documentoNombre: docEsp || (origen === "local" && docSeleccionado ? docSeleccionado : null),
      });

      toast.update(toastId, {
        render: "Resumen IA generado y guardado en Documentos",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      await cargarInfo();
      if (typeof onActualizarDocumentos === "function") {
        onActualizarDocumentos();
      }
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Error al generar el resumen IA",
        type: "error",
        isLoading: false,
        autoClose: 4500,
      });
    } finally {
      setGenerando(false);
    }
  };

  const handleSubirYResumir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSubiendoPdf(true);
    const toastId = toast.loading(`Subiendo ${file.name}...`);
    try {
      const up = await subirDocumento(numeroExpediente, file);
      toast.update(toastId, {
        render: "Documento subido. Generando resumen IA...",
        type: "info",
        isLoading: false,
        autoClose: 2000,
      });
      await cargarInfo();
      if (typeof onActualizarDocumentos === "function") {
        onActualizarDocumentos();
      }
      await handleGenerar("local", up.documento);
    } catch (err) {
      toast.update(toastId, {
        render: err.message || "Error subiendo el documento",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setSubiendoPdf(false);
      e.target.value = "";
    }
  };

  const handleCopiarResumen = () => {
    if (!infoIA?.resumen) return;
    navigator.clipboard.writeText(infoIA.resumen);
    toast.success("Resumen copiado al portapapeles");
  };

  const enviarPregunta = async (textoPregunta) => {
    const q = (textoPregunta ?? preguntaInput).trim();
    if (!q || enviandoChat) return;

    const nuevosMensajes = [...mensajes, { role: "user", content: q }];
    setMensajes(nuevosMensajes);
    setPreguntaInput("");
    setEnviandoChat(true);

    try {
      const res = await preguntarChatIA(
        numeroExpediente,
        q,
        nuevosMensajes.slice(-8),
        enlaceDefecto || infoIA?.enlace_principal || null
      );
      setMensajes((prev) => [
        ...prev,
        { role: "assistant", content: res.respuesta },
      ]);
      if (res.fuentes) {
        setFuentesChat(res.fuentes);
      }
    } catch (err) {
      toast.error(err.message || "Error al consultar al asistente IA");
      setMensajes((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `⚠️ No se pudo procesar la consulta: ${err.message}`,
          isError: true,
        },
      ]);
    } finally {
      setEnviandoChat(false);
    }
  };

  const secciones = parsearSeccionesResumen(infoIA?.resumen);
  const docsLocales = infoIA?.documentos_locales || [];
  const tieneEnlace = Boolean(enlaceDefecto || infoIA?.tiene_enlace);

  return (
    <div
      ref={panelRef}
      className="panel-ia-wrapper animate-fadeIn"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Cabecera del Panel IA */}
      <div className="panel-ia-header">
        <div className="panel-ia-title-group">
          <div className="panel-ia-badge-icon">
            <HiOutlineSparkles size={22} />
          </div>
          <div>
            <h3>
              Asistente IA del Expediente{" "}
              <span className="panel-ia-exp">
                #{numeroExpediente.toUpperCase()}
              </span>
            </h3>
            <p>
              Visor de resumen estructurado, análisis de PDFs locales y chat
              interactivo con los pliegos
            </p>
          </div>
        </div>

        <div className="panel-ia-tabs">
          <button
            type="button"
            className={`panel-ia-tab ${activeTab === "resumen" ? "active" : ""}`}
            onClick={() => setActiveTab("resumen")}
          >
            <HiOutlineDocumentText size={18} />
            <span>Visor Resumen IA</span>
            {infoIA?.resumen && <span className="tab-dot-ready" />}
          </button>
          <button
            type="button"
            className={`panel-ia-tab ${activeTab === "chat" ? "active" : ""}`}
            onClick={() => setActiveTab("chat")}
          >
            <HiOutlineChatAlt2 size={18} />
            <span>Chat con Pliegos</span>
          </button>
          {onCerrar && (
            <button
              type="button"
              className="btn-ia-close"
              onClick={onCerrar}
              title="Cerrar panel IA"
            >
              <HiX size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Contenido Pestaña 1: VISOR DE RESUMEN IA */}
      {activeTab === "resumen" && (
        <div className="panel-ia-body">
          {/* Barra de Herramientas y Fuentes de Generación */}
          <div className="panel-ia-toolbar">
            <div className="ia-source-controls">
              {docsLocales.length > 0 && (
                <div className="ia-local-group">
                  {docsLocales.length > 1 && (
                    <select
                      className="ia-doc-select"
                      value={docSeleccionado}
                      onChange={(e) => setDocSeleccionado(e.target.value)}
                      disabled={generando}
                    >
                      <option value="">
                        Todos los PDFs subidos ({docsLocales.length})
                      </option>
                      {docsLocales.map((doc, idx) => (
                        <option key={idx} value={doc}>
                          {doc}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    type="button"
                    className="btn-ia-action primary-local"
                    onClick={() => handleGenerar("local")}
                    disabled={generando}
                    title="Generar resumen directamente desde los documentos PDF subidos a esta licitación"
                  >
                    <HiOutlineFolderOpen size={17} />
                    <span>
                      {generando
                        ? "Procesando..."
                        : docSeleccionado
                        ? `Resumir ${docSeleccionado.slice(0, 22)}`
                        : `Resumir Docs Subidos (${docsLocales.length})`}
                    </span>
                  </button>
                </div>
              )}

              {tieneEnlace && (
                <button
                  type="button"
                  className="btn-ia-action secondary-web"
                  onClick={() => handleGenerar("enlace")}
                  disabled={generando}
                  title="Extraer documentación desde el enlace del portal de contratación"
                >
                  <HiOutlineGlobeAlt size={17} />
                  <span>
                    {infoIA?.resumen
                      ? "Regenerar desde Portal Web"
                      : "Resumir desde Portal Web"}
                  </span>
                </button>
              )}

              <button
                type="button"
                className="btn-ia-action upload-inline"
                onClick={() => fileUploadRef.current?.click()}
                disabled={generando || subiendoPdf}
                title="Subir un Pliego en PDF desde tu equipo y resumirlo al instante"
              >
                <HiOutlineCloudUpload size={17} />
                <span>{subiendoPdf ? "Subiendo..." : "Subir PDF y Resumir"}</span>
              </button>
              <input
                ref={fileUploadRef}
                type="file"
                accept=".pdf,.txt"
                style={{ display: "none" }}
                onChange={handleSubirYResumir}
              />
            </div>

            {infoIA?.resumen && (
              <div className="ia-export-controls">
                <button
                  type="button"
                  className="btn-ia-utility"
                  onClick={handleCopiarResumen}
                  title="Copiar texto al portapapeles"
                >
                  <HiOutlineClipboardCopy size={17} />
                  <span>Copiar</span>
                </button>
                {infoIA?.pdf_filename && (
                  <a
                    href={`/uploads/${encodeURIComponent(infoIA.pdf_filename)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ia-utility pdf-link"
                    title="Abrir o descargar el resumen en PDF"
                  >
                    <HiOutlineDownload size={17} />
                    <span>Descargar PDF</span>
                  </a>
                )}
                <button
                  type="button"
                  className="btn-ia-utility chat-jump"
                  onClick={() => setActiveTab("chat")}
                >
                  <HiOutlineChatAlt2 size={17} />
                  <span>Preguntar al Pliego</span>
                </button>
              </div>
            )}
          </div>

          {/* Estado de carga o visualización */}
          {cargandoInfo ? (
            <div className="panel-ia-loading">
              <div className="loader" />
              <p>Cargando análisis IA del expediente...</p>
            </div>
          ) : infoIA?.resumen ? (
            <>
              <div className="ia-meta-banner">
                <div className="ia-meta-tags">
                  {infoIA.origen && (
                    <span className="ia-tag-source">
                      Fuente: <strong>{infoIA.origen}</strong>
                    </span>
                  )}
                  {infoIA.updated_at && (
                    <span className="ia-tag-date">
                      Generado: {infoIA.updated_at}
                    </span>
                  )}
                </div>
              </div>

              {secciones.length > 1 ? (
                <div className="ia-secciones-grid">
                  {secciones.map((sec, idx) => (
                    <div
                      key={idx}
                      className={`ia-seccion-card ${
                        sec.contenido.join("\n").length > 260 ? "wide" : ""
                      }`}
                    >
                      <div className="ia-seccion-header">
                        <span className="ia-seccion-num">{sec.numero}</span>
                        <h4>{sec.titulo}</h4>
                      </div>
                      <div className="ia-seccion-content">
                        {sec.contenido
                          .join("\n")
                          .trim()
                          .split("\n")
                          .filter((l) => l.trim())
                          .map((linea, lIdx) => (
                            <p key={lIdx}>{linea.trim()}</p>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="ia-resumen-raw">
                  <pre>{infoIA.resumen}</pre>
                </div>
              )}
            </>
          ) : (
            <div className="panel-ia-empty">
              <div className="empty-sparkle-icon">
                <HiOutlineSparkles size={40} />
              </div>
              <h4>Todavía no se ha generado un resumen IA para este expediente</h4>
              <p>
                Puedes generarlo al instante a partir de los{" "}
                <strong>documentos PDF subidos localmente</strong> (PCAP, PPT,
                Cuadro de Características) o extrayendo los datos desde el{" "}
                <strong>enlace del portal oficial</strong>.
              </p>
              <div className="empty-ia-actions">
                {docsLocales.length > 0 && (
                  <button
                    type="button"
                    className="btn-nueva"
                    onClick={() => handleGenerar("local")}
                    disabled={generando}
                  >
                    <HiOutlineFolderOpen size={18} />
                    Resumir {docsLocales.length} documento(s) local(es)
                  </button>
                )}
                {tieneEnlace && (
                  <button
                    type="button"
                    className="btn-nueva"
                    onClick={() => handleGenerar("auto")}
                    disabled={generando}
                  >
                    <HiOutlineSparkles size={18} />
                    {generando ? "Generando resumen..." : "Generar Resumen IA Ahora"}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-gestion"
                  onClick={() => fileUploadRef.current?.click()}
                  disabled={generando || subiendoPdf}
                >
                  <HiOutlineCloudUpload size={18} />
                  Subir Pliego PDF y Resumir
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contenido Pestaña 2: CHAT INTERACTIVO CON LOS PLIEGOS */}
      {activeTab === "chat" && (
        <div className="panel-ia-chat-container">
          {/* Barra informativa de fuentes */}
          <div className="chat-ia-sources-bar">
            <div className="sources-list">
              <span className="sources-label">Contexto activo:</span>
              <span className="source-pill">Datos del expediente</span>
              {docsLocales.length > 0 && (
                <span className="source-pill highlight">
                  📄 {docsLocales.length} PDF(s) local(es):{" "}
                  {docsLocales.slice(0, 2).join(", ")}
                  {docsLocales.length > 2 ? "..." : ""}
                </span>
              )}
              {infoIA?.resumen && (
                <span className="source-pill highlight">✨ Resumen y Pliego IA</span>
              )}
              {fuentesChat
                .filter(
                  (f) =>
                    !f.includes("Datos") &&
                    !f.includes("Docs locales") &&
                    !f.includes("Resumen IA")
                )
                .map((f, i) => (
                  <span key={i} className="source-pill">
                    {f}
                  </span>
                ))}
            </div>
            {mensajes.length > 0 && (
              <button
                type="button"
                className="btn-clear-chat"
                onClick={() => setMensajes([])}
                title="Limpiar conversación"
              >
                <HiOutlineTrash size={15} /> Limpiar chat
              </button>
            )}
          </div>

          {/* Sugerencias rápidas */}
          <div className="chat-ia-suggestions">
            {PREGUNTAS_RAPIDAS.map((sug, idx) => (
              <button
                key={idx}
                type="button"
                className="suggestion-chip"
                onClick={() => enviarPregunta(sug)}
                disabled={enviandoChat}
              >
                {sug}
              </button>
            ))}
          </div>

          {/* Historial de Mensajes */}
          <div className="chat-ia-messages" ref={messagesBoxRef}>
            {mensajes.length === 0 ? (
              <div className="chat-ia-welcome">
                <HiOutlineChatAlt2 size={36} />
                <h4>Pregunta cualquier detalle sobre los Pliegos</h4>
                <p>
                  El asistente analiza los documentos PDF subidos al expediente,
                  el contenido extraído del portal y las condiciones técnicas o
                  administrativas para responder tus dudas al instante.
                </p>
              </div>
            ) : (
              mensajes.map((msg, idx) => (
                <div
                  key={idx}
                  className={`chat-bubble-row ${
                    msg.role === "user" ? "user" : "assistant"
                  }`}
                >
                  <div
                    className={`chat-bubble ${
                      msg.role === "user" ? "user" : "assistant"
                    } ${msg.isError ? "error" : ""}`}
                  >
                    <div className="chat-bubble-header">
                      <span>
                        {msg.role === "user" ? "Tú" : "✨ Asistente de Pliegos"}
                      </span>
                      {msg.role === "assistant" && !msg.isError && (
                        <button
                          type="button"
                          className="btn-copy-msg"
                          onClick={() => {
                            navigator.clipboard.writeText(msg.content);
                            toast.info("Respuesta copiada");
                          }}
                          title="Copiar respuesta"
                        >
                          <HiOutlineClipboardCopy size={14} />
                        </button>
                      )}
                    </div>
                    <div className="chat-bubble-text">
                      {msg.content.split("\n").map((line, lIdx) => (
                        <p key={lIdx}>{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}

            {enviandoChat && (
              <div className="chat-bubble-row assistant">
                <div className="chat-bubble assistant typing">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span style={{ marginLeft: 8, fontSize: "0.82rem" }}>
                    Consultando pliegos con Gemini...
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Input de envío */}
          <form
            className="chat-ia-input-bar"
            onSubmit={(e) => {
              e.preventDefault();
              enviarPregunta();
            }}
          >
            <input
              type="text"
              placeholder="Escribe tu pregunta sobre el pliego (ej. ¿Piden clasificación empresarial?, ¿Cuál es el plazo de garantía?)..."
              value={preguntaInput}
              onChange={(e) => setPreguntaInput(e.target.value)}
              disabled={enviandoChat}
            />
            <button
              type="submit"
              className="btn-send-chat"
              disabled={!preguntaInput.trim() || enviandoChat}
            >
              <HiOutlinePaperAirplane
                size={18}
                style={{ transform: "rotate(90deg)" }}
              />
              <span>Preguntar</span>
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export default PanelIA;

